import { call, fork, take, put } from 'redux-saga/effects'
import { bindAsyncAction } from 'typescript-fsa-redux-saga'
import { Action } from 'typescript-fsa'
import i18n from 'i18next'
import { FacebookPostApi, FacebookPageApi, PostApi } from '../../api/instances'
import { Severity, EventType } from '@professocial/professocialTypes/types/messageTypes'
import {
  GetFacebookFeedForPageRequest,
  LikeFacebookPostRequest,
  UnlikeFacebookPostRequest,
  WriteFacebookPostCommentRequest,
  AnswerToFacebookPostCommentRequest,
  LikeFacebookPostCommentRequest,
  UnlikeFacebookPostCommentRequest,
  GetFacebookPostRequest, DeleteFacebookPostCommentRequest,
} from '@3m5/sm_publish-frontend-api/apis/FacebookPostApi'
import {
  getFacebookFeedForPage,
  getFacebookFeedForPageInitiated,
  deletePost,
  deletePostInitiated,
  likeFacebookPost,
  likeFacebookPostInitiated,
  unlikeFacebookPost,
  unlikeFacebookPostInitiated,
  writeFacebookPostComment,
  writeFacebookPostCommentInitiated,
  answerToFacebookPostComment,
  answerToFacebookPostCommentInitiated,
  likeFacebookPostCommentInitiated,
  likeFacebookPostComment,
  unlikeFacebookPostCommentInitiated,
  unlikeFacebookPostComment,
  getFacebookPost,
  getFacebookPostInitiated,
  getFacebookPageInitiated,
  getFacebookPage, deleteFacebookPostComment, deleteFacebookPostCommentInitiated,
} from './facebook.actions'
import { requestFailed, requestFinished, requestStarted, setMessage } from '../common/common.actions'
import { GetFacebookPageRequest } from '@3m5/sm_publish-frontend-api/apis/FacebookPageApi'
import { DeletePostRequest, NetworkType } from '@3m5/sm_publish-frontend-api'

// getFacebookPage
const getFacebookPageWorker = bindAsyncAction(getFacebookPage)(
  function * (params: GetFacebookPageRequest) {
    return yield call([FacebookPageApi, FacebookPageApi.getFacebookPage], params)
  },
)

function * watchGetFacebookPageInitiated () {
  while (true) {
    const action: Action<GetFacebookPageRequest> = yield take(getFacebookPageInitiated)
    try {
      yield getFacebookPageWorker(action.payload)
    } catch (e) {
      yield put(setMessage({
        severity: Severity.error,
        message: i18n.t('postMessages:loadError', { context: i18n.t('postCommon:facebookPage') }),
        eventType: EventType.load,
      }))
      console.error('error in watchGetFacebookPageInitiated: ', e)
    }
  }
}

// getFacebookPost
const getFacebookPostWorker = bindAsyncAction(getFacebookPost)(
  function * (params: GetFacebookPostRequest) {
    return yield call([FacebookPostApi, FacebookPostApi.getFacebookPost], params)
  },
)

function * watchGetFacebookPostInitiated () {
  while (true) {
    const action: Action<GetFacebookPostRequest> = yield take(getFacebookPostInitiated)
    try {
      yield getFacebookPostWorker(action.payload)
    } catch (e) {
      yield put(setMessage({
        severity: Severity.error,
        message: i18n.t('postMessages:loadError', { context: i18n.t('postCommon:post') }),
        eventType: EventType.load,
      }))
      console.error('error in watchGetFacebookPostInitiated: ', e)
    }
  }
}

// loadFacebookFeedForPage
const loadFacebookFeedForPageWorker = bindAsyncAction(getFacebookFeedForPage)(
  function * (params: GetFacebookFeedForPageRequest) {
    return yield call([FacebookPostApi, FacebookPostApi.getFacebookFeedForPage], params)
  },
)

function * watchLoadFacebookFeedForPageInitiated () {
  while (true) {
    const action: Action<{ data: GetFacebookFeedForPageRequest, id: string }> = yield take(getFacebookFeedForPageInitiated)
    yield put(requestStarted({ type: action.type, id: action.payload.id }))
    try {
      yield loadFacebookFeedForPageWorker(action.payload.data)
      yield put(requestFinished({ type: action.type, id: action.payload.id }))
    } catch (e) {
      yield put(requestFailed({
        type: action.type,
        id: action.payload.id,
        error: e,
        message: {
          severity: Severity.error,
          message: i18n.t('postMessages:loadingError', { context: i18n.t('postCommon:posts') }),
          eventType: EventType.load,
        },
      }))
      console.error('error in watchLoadFacebookFeedForPageInitiated: ', e)
    }
  }
}

// deletePost
const deletePostWorker = bindAsyncAction(deletePost)(
  function * (params: DeletePostRequest) {
    return yield call([PostApi, PostApi.deletePost], params)
  },
)

function * watchDeletePostInitiated () {
  while (true) {
    const action: Action<DeletePostRequest> = yield take(deletePostInitiated)
    yield put(requestStarted({ type: action.type, id: action.payload.networkPostId, network: NetworkType.Facebook }))
    try {
      yield deletePostWorker(action.payload)
      yield put(requestFinished({
        type: action.type,
        id: action.payload.networkPostId,
        message: {
          severity: Severity.success,
          message: i18n.t('postMessages:isSuccessfullyDeleted', { context: i18n.t('postCommon:post') }),
          eventType: EventType.delete,
        },
      }))
    } catch (e) {
      yield put(requestFailed({
        type: action.type,
        id: action.payload.networkPostId,
        error: e,
        message: {
          severity: Severity.error,
          message: i18n.t('postMessages:deleteError', { context: i18n.t('postCommon:post') }),
          eventType: EventType.delete,
        },
      }))
      console.error('error in watchDeletePostInitiated: ', e)
    }
  }
}

// deletePostComment
const deletePostCommentWorker = bindAsyncAction(deleteFacebookPostComment)(
  function * (params: DeleteFacebookPostCommentRequest) {
    return yield call([FacebookPostApi, FacebookPostApi.deleteFacebookPostComment], params)
  },
)

function * watchDeletePostCommentInitiated () {
  while (true) {
    const action: Action<DeleteFacebookPostCommentRequest> = yield take(deleteFacebookPostCommentInitiated)
    yield put(requestStarted({ type: action.type, id: action.payload.networkCommentId, network: NetworkType.Facebook }))
    try {
      yield deletePostCommentWorker(action.payload)
      yield put(requestFinished({
        type: action.type,
        id: action.payload.networkCommentId,
        message: {
          severity: Severity.success,
          message: i18n.t('postMessages:isSuccessfullyDeleted', { context: i18n.t('postCommon:comment') }),
          eventType: EventType.delete,
        },
      }))
    } catch (e) {
      yield put(requestFailed({
        type: action.type,
        id: action.payload.networkCommentId,
        error: e,
        message: {
          severity: Severity.error,
          message: i18n.t('postMessages:deleteError', { context: i18n.t('postCommon:comment') }),
          eventType: EventType.delete,
        },
      }))
      console.error('error in watchDeletePostCommentInitiated: ', e)
    }
  }
}

// likePost
const likePostWorker = bindAsyncAction(likeFacebookPost)(
  function * (params: LikeFacebookPostRequest) {
    return yield call([FacebookPostApi, FacebookPostApi.likeFacebookPost], params)
  },
)

function * watchLikePostInitiated () {
  while (true) {
    const action: Action<LikeFacebookPostRequest> = yield take(likeFacebookPostInitiated)
    yield put(requestStarted({ type: action.type, id: action.payload.networkPostId }))
    try {
      yield likePostWorker(action.payload)
      yield put(requestFinished({ type: action.type, id: action.payload.networkPostId }))
    } catch (e) {
      yield put(requestFailed({
        type: action.type,
        id: action.payload.networkPostId,
        error: e,
        message: {
          severity: Severity.error,
          message: i18n.t('postMessages:updateError', { context: i18n.t('postCommon:post') }),
          eventType: EventType.update,
        },
      }))
      console.error('error in watchLikePostInitiated: ', e)
    }
  }
}

// unlikePost
const unlikePostWorker = bindAsyncAction(unlikeFacebookPost)(
  function * (params: UnlikeFacebookPostRequest) {
    return yield call([FacebookPostApi, FacebookPostApi.unlikeFacebookPost], params)
  },
)

function * watchUnlikePostInitiated () {
  while (true) {
    const action: Action<UnlikeFacebookPostRequest> = yield take(unlikeFacebookPostInitiated)
    yield put(requestStarted({ type: action.type, id: action.payload.networkPostId }))
    try {
      yield unlikePostWorker(action.payload)
      yield put(requestFinished({ type: action.type, id: action.payload.networkPostId }))
    } catch (e) {
      yield put(requestFailed({
        type: action.type,
        id: action.payload.networkPostId,
        error: e,
        message: {
          severity: Severity.error,
          message: i18n.t('postMessages:updateError', { context: i18n.t('postCommon:post') }),
          eventType: EventType.update,
        },
      }))
      console.error('error in watchUnlikePostInitiated: ', e)
    }
  }
}

// writePostComment
const writePostCommentWorker = bindAsyncAction(writeFacebookPostComment)(
  function * (params: WriteFacebookPostCommentRequest) {
    return yield call([FacebookPostApi, FacebookPostApi.writeFacebookPostComment], params)
  },
)

function * watchWritePostCommentInitiated () {
  while (true) {
    const action: Action<WriteFacebookPostCommentRequest> = yield take(writeFacebookPostCommentInitiated)
    yield put(requestStarted({ type: action.type, id: action.payload.networkPostId }))
    try {
      yield writePostCommentWorker(action.payload)
      yield put(requestFinished({
        type: action.type,
        id: action.payload.networkPostId,
        message: {
          severity: Severity.success,
          message: i18n.t('postMessages:isSuccessfullyCreated', { context: i18n.t('postCommon:comment') }),
          eventType: EventType.create,
        },
      }))
    } catch (e) {
      yield put(requestFailed({
        type: action.type,
        id: action.payload.networkPostId,
        error: e,
        message: {
          severity: Severity.error,
          message: i18n.t('postMessages:createError', { context: i18n.t('postCommon:comment') }),
          eventType: EventType.create,
        },
      }))
      console.error('error in watchWritePostCommentInitiated: ', e)
    }
  }
}

// answerToPostComment
const answerToPostCommentWorker = bindAsyncAction(answerToFacebookPostComment)(
  function * (params: AnswerToFacebookPostCommentRequest) {
    return yield call([FacebookPostApi, FacebookPostApi.answerToFacebookPostComment], params)
  },
)

function * watchAnswerToPostCommentInitiated () {
  while (true) {
    const action: Action<AnswerToFacebookPostCommentRequest> = yield take(answerToFacebookPostCommentInitiated)
    try {
      yield answerToPostCommentWorker(action.payload)
    } catch (e) {
      yield put(setMessage({
        severity: Severity.error,
        message: i18n.t('postMessages:createError', { context: i18n.t('postCommon:comment') }),
        eventType: EventType.create,
      }))
      console.error('error in watchAnswerToPostCommentInitiated: ', e)
    }
  }
}

// likePostComment
const likePostCommentWorker = bindAsyncAction(likeFacebookPostComment)(
  function * (params: LikeFacebookPostCommentRequest) {
    return yield call([FacebookPostApi, FacebookPostApi.likeFacebookPostComment], params)
  },
)

function * watchLikePostCommentInitiated () {
  while (true) {
    const action: Action<LikeFacebookPostCommentRequest> = yield take(likeFacebookPostCommentInitiated)
    try {
      yield likePostCommentWorker(action.payload)
    } catch (e) {
      yield put(setMessage({
        severity: Severity.error,
        message: i18n.t('postMessages:updateError', { context: i18n.t('postCommon:like') }),
        eventType: EventType.update,
      }))
      console.error('error in watchLikePostCommentInitiated: ', e)
    }
  }
}

// unlikePostComment
const unlikePostCommentWorker = bindAsyncAction(unlikeFacebookPostComment)(
  function * (params: UnlikeFacebookPostCommentRequest) {
    return yield call([FacebookPostApi, FacebookPostApi.unlikeFacebookPostComment], params)
  },
)

function * watchUnlikePostCommentInitiated () {
  while (true) {
    const action: Action<UnlikeFacebookPostCommentRequest> = yield take(unlikeFacebookPostCommentInitiated)
    try {
      yield unlikePostCommentWorker(action.payload)
    } catch (e) {
      yield put(setMessage({
        severity: Severity.error,
        message: i18n.t('postMessages:updateError', { context: i18n.t('postCommon:unlike') }),
        eventType: EventType.update,
      }))
      console.error('error in watchUnlikePostCommentInitiated: ', e)
    }
  }
}

export function * facebookSagas () {
  yield fork(watchGetFacebookPageInitiated)
  yield fork(watchLoadFacebookFeedForPageInitiated)
  yield fork(watchGetFacebookPostInitiated)
  yield fork(watchDeletePostInitiated)
  yield fork(watchDeletePostCommentInitiated)
  yield fork(watchLikePostInitiated)
  yield fork(watchUnlikePostInitiated)
  yield fork(watchWritePostCommentInitiated)
  yield fork(watchAnswerToPostCommentInitiated)
  yield fork(watchLikePostCommentInitiated)
  yield fork(watchUnlikePostCommentInitiated)
}
